import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, OnDestroy, OnInit, PLATFORM_ID, signal, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { Subscription, finalize, take } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SharedService } from '../shared/services/shared.service';
import { User } from '../model/user.model';
import { ToasterService } from '../shared/services/toaster.service';
import { TokenService } from '../auth/token.service';
import { HeaderComponent } from './header/header.component';
import { ChatBoxComponent } from '../modules/chat-box/chat-box.component';
import { NgbModal, NgbModalModule, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../environments/environment';
import { CourseService } from '../services/course.service';
import { PersonalizationService } from '../services/personalization.service';
import { ScrollChainingDirective } from '../shared/directives/scroll-chaining.directive';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-layout',
    imports: [
        RouterOutlet,
        RouterModule,
        CommonModule,
        // AuthModalComponent,
        HeaderComponent,
        ChatBoxComponent,
        NgbModalModule,
        ScrollChainingDirective
    ],
    providers: [],
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
  claimModalRef: NgbModalRef | undefined;
  // authPage = 'login';
  // openModal = false;
  thisYear = new Date().getFullYear();
  @ViewChild('courseInvitationModal', { static: true }) courseInvitationModal: TemplateRef<unknown> | undefined;
  expandMenu = false;
  private _subscription!: Subscription;
  user = signal<User | null>(null);
  prevUrl: string = '';
  currentUrl: string = '';
  isMobileMenuActive = false;
  isSignedIn = false;
  activeTab = '';
  hideExceptTopMenu = false;
  inEmbeddedBrowser = false;
  loadCaddyData = false;
  mobileSlideMenu = false;
  invitedBusinessName = '';
  claimModalSubmitting = false;
  businessClaimed = false;

  private _sharedService = inject(SharedService);
  private router = inject(Router);
  private platformId = inject<object>(PLATFORM_ID);
  private _activatedRoute = inject(ActivatedRoute);
  private _toaster = inject(ToasterService);
  private _tokenService = inject(TokenService);
  private _cdr = inject(ChangeDetectorRef);
  private _modalService = inject(NgbModal);
  private _courseService = inject(CourseService);

  protected readonly personalization = inject(PersonalizationService);
  protected isLandingRoute = false;

  private syncLandingScrollbarTheme(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Keep the fixed custom scrollbar on landing routes. Colours are no longer
    // course-driven, so no theme classes or CSS variables are applied here.
    document.documentElement.classList.toggle('landing-scrollbar-theme', this.isLandingRoute);
  }
  
  constructor() {
    effect(() => {
      this.isSignedIn = this._sharedService.loggedIn();
      if(this.isSignedIn) {
        this._activatedRoute.queryParams.subscribe(params => {
          if(params['source'] === 'course-invitation') {
            this.openClaimModal();
          }
        });
      }
    });
    
    // this._sharedService.openLoginModal$.subscribe(modal => {
    //   if (modal.status && this.authModal) {
    //     this.openAuthModal(this.authModal, modal.window);
    //   }
    // })
    this.router.events.subscribe((val) => {
      if (isPlatformBrowser(this.platformId)) {
        if (val instanceof NavigationEnd) {
          this.isLandingRoute = val.urlAfterRedirects === '/' || val.urlAfterRedirects.startsWith('/home');
          this.syncLandingScrollbarTheme();
          const navigation = this.router.currentNavigation();
          this.hideExceptTopMenu = navigation?.extras.state?.['hideExceptTopMenu'] || false;
          this._cdr.markForCheck();
          this.expandMenu = false;
          this.mobileSlideMenu = false;
          this.isMobileMenuActive = false;
          if (!val.url.includes('/settings')) {
            this.activeTab = '';
          } else if (val.url.includes('?tab=MyBag')) {
            this.activeTab = 'MyBag';
          } else if (val.url.includes('?tab=MyPartner')) {
            this.activeTab = 'MyPartner';
          }
        }
      }
    });
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this._activatedRoute.queryParams.subscribe(params => {
        if (params['error']) {
          this._toaster.error(params['error'], 'Please try again');
          this.clearQueryParams();
        }
        if (params['page'] === 'register') {
          if (params['access_token'] && params['id_token'] && params['refresh_token']) {
            this._tokenService.saveTokens({
              AccessToken: params['access_token'],
              IdToken: params['id_token'],
              RefreshToken: params['refresh_token']
            });
            this.loadCaddyData = true;
            // this.getUserSetting();
          }
        }
        if (params['type'] === 'register') {
          this.router.navigate(['/register']);
        } else if (params['type'] === 'login') {
          this.router.navigate(['/login']);
        }
        // Redirect already logged-in users to course portal
        if (params['source'] === 'course-portal' && this._sharedService.loggedIn()) {
          window.location.href = environment.coursePortalUrl;
        }
      });
      if (this.isEmbeddedBrowser()) {
        this.inEmbeddedBrowser = true;
      }

      this._sharedService.isMyBagActive$.subscribe(res => {
        if (res) {
          this.activeTab = res;
        }
      })
    }
  }

  clearQueryParams() {
    this.router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: {},
      queryParamsHandling: ''
    });
  }

  navigateToSettingPage(tabName: string) {
    // this._sharedService.setByBagSettingTab(tabName);
    // this.router.navigateByUrl("/settings");
    this.router.navigate(["settings/user-settings"], {
      queryParams: { tab: tabName }, // Set the new query parameter
      queryParamsHandling: 'merge', // Merge with existing query params
    });
    this.activeTab = tabName;
    this.expandMenu = false;
    this.mobileSlideMenu = false;
    this.isMobileMenuActive = false;
  }

  isEmbeddedBrowser(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;

    // Detect LinkedIn, Facebook, Instagram embedded browsers
    return /FBAN|FBAV|Instagram|LinkedIn/i.test(userAgent);
  }

  // openAuthModal(content: any, modalPage: string) {
  //   if (isPlatformBrowser(this.platformId)) {
  //     if (this.router.url.includes('answer') || this.router.url.includes('discover')) {
  //       localStorage.setItem('redirectUrl', this.router.url);
  //     }
  //     this.authPage = modalPage;
  //     this.authModalRef = this._modalService.open(content, { windowClass: 'auth-modal' });
  //     this.clearQueryParams();
  //   }
  // }

  // closeAuthModal(event: any) {
  //   this.authModalRef?.dismiss();
  // }

    openClaimModal() {
    if (isPlatformBrowser(this.platformId)) {
      this.invitedBusinessName = this._sharedService.getLocalStorage('invitedBusinessName') || '';
      this.claimModalRef = this._modalService.open(
        this.courseInvitationModal,
        { windowClass: 'claim-modal', backdrop: 'static', centered: true });
      // this.clearQueryParams();
    }
  }

  onClaimNow() {
    const inviteSessionToken = this._sharedService.getLocalStorage('inviteSessionToken') || '';
    if (!inviteSessionToken) {
      this._toaster.error('Invalid or expired invitation. Please try the invitation link again.', 'Error');
      return;
    }
    this.claimModalSubmitting = true;
    this._courseService
      .claimCourse(inviteSessionToken)
      .pipe(finalize(() => { this.claimModalSubmitting = false; }))
      .subscribe({
        next: (res) => {
          this._toaster.success('Course claimed successfully', 'Success');
          this.businessClaimed = true;
          this.clearQueryParams();
          // Mark user as having course access
          this._sharedService.user$.pipe(take(1)).subscribe((u) => {
            if (u) {
              this._sharedService.setUser({ ...u, hasCourse: true });
            }
          });
          this._sharedService.removeFromLocalStorage('inviteSessionToken');
          this._sharedService.removeFromLocalStorage('invitedBusinessName');
        },
        error: (error) => {
          // this._toaster.error('Failed to claim course. Please try again later.', 'Error');
        }
      });
  }

  closeClaimModal() {
    this.claimModalRef?.dismiss();
  }

  goToCoursePortal() {
    this.closeClaimModal();
    window.open(environment.coursePortalUrl, '_blank');
  }

  ngOnDestroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
}
